import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { GeneralLedgerSidepanel, PermissionEnum } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearTransactionState } from '../store/slices/gl-transaction';
import useGetGLFunctions from './hooks/use-get-gl-functions';

import ROUTES from '../../../routes';

const GLTransactionHOC = (): React.JSX.Element => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);

  const { transactionTypes } = useAppSelector(
    (state) => state.accountsDictionaries,
  );
  const { gl } = useAppSelector(
    (state) => state.accountsStatements.transactions,
  );
  const {
    baseCurrency,
    activePermissions,
    currencies: currenciesList,
  } = useAppSelector((state) => state.root);
  const { users } = useAppSelector((state) => state.administrationDictionaries);

  const { transactionId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const {
    isLoading,
    isFetching,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    linkedTransactionPath,
    getTransactionChangeLogsAction,
    changeLogsIsFetching,
    changeLogsIsLoading,
    getTransationDocumentsAction,
    documentsIsLoading,
    documentsIsFetching,
    attachedDocumentsErrors,
    uploadedDocumentsErrors,
    uploadedFilesError,
    imgLink,
    addDocumentInProgress,
    fileUploadIsSuccess,
    checkDocumentNameInProgress,
    fileUploadInProgress,
    onUploadDocument,
    documentIsLoading,
    documentIsFetching,
    downloadInProgress,
    downloadIsFetching,
    downloadFileIsSuccess,
    getTransactionDocumentByIdAction,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    onDeleteDocument,
    onChangeCustomerDocumentsStatus,
    documentStatusChangeInProgress,
    documentCategoryChangeInProgress,
    onMoveDocument,
    commentInProgress,
    onSendDocumentComment,
    onEditComment,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteComment,
    deleteCommentInProgress,
    onDownloadDocument,
  } = useGetGLFunctions({
    transactionTypes,
    gl,
    transactionId,
    activePermissions,
  });

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(`${ROUTES.STATEMENTS_OF_ACCOUNTS}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  return (
    <GeneralLedgerSidepanel
      panelIsOpen={panelIsOpen}
      handleClose={handleClose}
      transaction={gl.transaction}
      isLoading={isLoading || isFetching}
      title="GL Transaction"
      linkedTransactionPath={
        linkedTransactionPath.includes('#') ? undefined : linkedTransactionPath
      }
      debitAccountBalance={gl?.debitAccountBalance}
      creditAccountBalance={gl?.creditAccountBalance}
      debitBalanceIsLoading={debitBalanceIsLoading || debitBalanceIsFetching}
      creditBalanceIsLoading={creditBalanceIsLoading || creditBalanceIsFetching}
      permissionDenied={
        !activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_LIST,
        )
      }
      baseCurrency={baseCurrency}
      currenciesList={currenciesList}
      sidepanelExpandedKey="statements-gl"
      activePermission={activePermissions}
      users={users}
      changeLogsIsLoading={changeLogsIsLoading}
      changeLogsIsFetching={changeLogsIsFetching}
      getTransactionChangeLogs={getTransactionChangeLogsAction}
      transactionChangeLogsPagination={gl.transactionChangeLogsPagination}
      changeLogs={gl.transactionChangeLogs}
      getTransationDocumentsAction={getTransationDocumentsAction}
      documentsIsLoading={documentsIsLoading}
      documentsIsFetching={documentsIsFetching}
      documents={gl.documents}
      documentsPagination={gl.documentsPagination}
      attachedDocumentsErrors={attachedDocumentsErrors}
      uploadedDocumentsErrors={uploadedDocumentsErrors}
      uploadDocumentErrors={uploadedFilesError}
      uploadDocumentInProgress={
        checkDocumentNameInProgress ||
        fileUploadInProgress ||
        addDocumentInProgress
      }
      uploadDocumentIsSuccess={fileUploadIsSuccess}
      onUploadDocument={onUploadDocument}
      onDownloadDocument={onDownloadDocument}
      onDownloadSuccess={downloadFileIsSuccess}
      downloadInProgress={downloadInProgress || downloadIsFetching}
      documentFileIsLoading={downloadInProgress || downloadIsFetching}
      documentItem={gl.document}
      documentIsLoading={documentIsLoading || documentIsFetching}
      imgLink={imgLink}
      getTransactionDocument={getTransactionDocumentByIdAction}
      documentStatusChangeInProgress={documentStatusChangeInProgress}
      changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
      onMove={onMoveDocument}
      moveInProgress={documentCategoryChangeInProgress}
      deleteIsVisible={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DOCUMENTS_DELETE_ACTION,
      )}
      commentInProgress={commentInProgress}
      sendComment={onSendDocumentComment}
      editCommentInProgress={editCommentInProgress}
      editCommentIsSuccess={editCommentIsSuccess}
      editComment={onEditComment}
      deleteCommentInProgress={deleteCommentInProgress}
      deleteComment={onDeleteComment}
      onDeleteDocument={onDeleteDocument}
      deleteInProgress={deleteDocumentInProgress}
      deleteIsSuccess={deleteDocumentIsSuccess}
    />
  );
};

export default GLTransactionHOC;
